import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import {
  Badge,
  Button,
  Drawer,
  Grid,
  Icon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Toolbar,
  Tooltip
} from '@material-ui/core';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import { BackdropCircular, Breadcrumb } from 'components';
import { downloadAutoQuotesCustomersApi, getAutoQuotesCustomersApi } from 'api/automatedQuotes';
import useLocalStorageTableCells from 'hooks/useLocalStorageTableCells';
import ViewColumn from 'helpers/ViewColumn';
import { defaultCustomerTableCells } from '../helpers';
import { useStyles } from '../styles';
import CustomerTableRow from './CustomerTableRow';
import FilterDrawer from './FilterDrawer';


const CustomerPreview = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const { id } = useParams<{ [id: string]: string }>();
  const [sortDirection, setSortDirection] = useState('desc');
  const [sortField, setSortField] = useState('created_at');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openFilter, setOpenFilter] = useState<boolean>(false);
  const [filterConditions, setFilterConditions] = useState<IFilterConditions>({});
  const { headTableCells, setHeadTableCells } = useLocalStorageTableCells('automatedQuotesCustomersTableCells', defaultCustomerTableCells);

  const {
    autoQuotesCustomersList,
    autoQuotesCustomersListLoading,
    autoQuoteCustomersTotal,
    isDownloadAutoQuotesCustomersReport
  } = useSelector((state: IReduxState) => state.automatedQuotes);

  useEffect(() => {
    getAutoQuotesCustomersApi({
      dispatch,
      id,
      sortDirection,
      sortField,
      pageSize: rowsPerPage,
      currentPage,
      filterConditions
    });
  }, [
    sortDirection,
    sortField,
    rowsPerPage,
    currentPage,
    id,
    filterConditions
  ]);

  const handleRequestSort = (event, property) => {
    const isDesc = sortField === property && sortDirection === 'desc';
    setSortDirection(isDesc ? 'asc' : 'desc');
    setSortField(property);
  };

  const handleChangePage = (event, newPage) => {
    setCurrentPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(1);
  };

  const handleProceed = () => {
    history.push(`/automated-quotes/${id}/customer-preview/workspace`);
  };

  const selectedConditions = Object.entries(filterConditions)
    .filter(([_key, value]) => value !== undefined && value !== '');

  return (
    <div className="flex-grow h-full min-h-full-screen scroll-y pb-25">
      <div className="mb-sm-30">
        <Breadcrumb
          routeSegments={[
            { name: 'Automated Quotes', path: '/automated-quotes'},
            { name: 'Customer Preview'}
          ]}
        />
      </div>
      <div className={classes.card}>
        <div className={`py-4 ${classes.toolbarContainer}`}>
          <Toolbar className="w-full relative">
            <Grid item xs={12} className="mt-4">
              <Button
                variant="contained"
                color="primary"
                onClick={handleProceed}
              >
                Proceed to Quote Configuration
              </Button>
              <Tooltip title="Download">
                <Button
                  variant="outlined"
                  color="primary"
                  disabled={!autoQuotesCustomersList?.length}
                  className={`${classes.filterBtn} ml-2`}
                  onClick={() => downloadAutoQuotesCustomersApi({ dispatch, id })}
                >
                  <Icon className={`${!!autoQuotesCustomersList?.length ? 'text-primary' : null} mr-2`}>
                    cloud_download
                  </Icon>
                  <span className={`${!!autoQuotesCustomersList?.length ? 'text-brand' : ''}`}>
                    Download
                  </span>
                </Button>
              </Tooltip>
            </Grid>
            <ViewColumn
              storageKey="automatedQuotesCustomersTableCells"
              tableCells={defaultCustomerTableCells}
              setHeadTableCells={setHeadTableCells}
            />
            <Badge
              badgeContent={selectedConditions.length}
              color="primary"
              overlap="rectangular"
            >
              <Tooltip title="Filter by">
                <Button
                  variant="outlined"
                  color="primary"
                  className={classes.filterBtn}
                  onClick={() => setOpenFilter(true)}
                >
                  <Icon className="text-primary mr-2">
                    filter_list
                  </Icon>
                  <span className="text-brand">Filter By</span>
                </Button>
              </Tooltip>
            </Badge>
          </Toolbar>
        </div>
        <div className="table-container">
          <TableContainer>
            <Table aria-label="collapsible table" className={classes.table}>
              <TableHead>
                <TableRow>
                  {headTableCells.map((cell, index) => {
                    if (!cell.display) {
                      return null;
                    }

                    return (
                      <TableCell key={index} className={`min-w-${cell.width}`}>
                        {cell.sortField ? (
                          <TableSortLabel
                            active={sortField === cell.sortField}
                            IconComponent={sortDirection === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon}
                            onClick={(event) => handleRequestSort(event, cell.sortField)}
                          >
                            {cell.label}
                          </TableSortLabel>
                        ) : (
                          cell.label
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {!autoQuoteCustomersTotal ?
                  <TableRow>
                    <TableCell colSpan={Object.keys(headTableCells).length + 1} align="center">
                      No matching customers found.
                    </TableCell>
                  </TableRow>
                  :
                  autoQuotesCustomersList.map((customer) => (
                    <CustomerTableRow
                      key={customer.id}
                      customerData={customer}
                      headTableCells={headTableCells}
                    />
                  ))
                }
              </TableBody>
            </Table>
          </TableContainer>
          {!!autoQuoteCustomersTotal &&
            <TablePagination
              rowsPerPageOptions={[10, 20, 40, 80, 100]}
              component="div"
              className={classes.paginationContainer}
              count={autoQuoteCustomersTotal}
              rowsPerPage={rowsPerPage}
              page={currentPage - 1}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          }
        </div>
      </div>
      <Drawer
        anchor="right"
        open={openFilter}
        onClose={() => setOpenFilter((openFilter) => !openFilter)}
        PaperProps={{ style: { maxWidth: '400px' } }}
      >
        <FilterDrawer
          setOpenFilter={setOpenFilter}
          setFilterConditions={setFilterConditions}
          filterConditions={filterConditions}
        />
      </Drawer>
      <BackdropCircular loading={autoQuotesCustomersListLoading || isDownloadAutoQuotesCustomersReport} />
    </div>
  );
};

export default CustomerPreview;
